import { useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  TextField,
  Button,
  Stack,
  Link as MUILink,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import useUser from "../hooks/user";
import LoginFooter from "../components/loginFooter";
import logo from "../assets/img/smartTrail.svg";

export default function ResetPassword() {
  const { uid, token } = useParams();
  const user = useUser();

  const [pass, setPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const validatePassword = (password) => {
    if (!/[0-9]/.test(password)) {
      return "Password should contain at least one numeric digit.";
    } else if (!/[A-Z]/.test(password)) {
      return "Password should contain at least one uppercase letter.";
    } else if (!/[a-z]/.test(password)) {
      return "Password should contain at least one lowercase letter.";
    } else if (!/[!@#$%^&*(),.?\":{}|<>]/.test(password)) {
      return "Password should contain at least one special character.";
    } else if (password.length < 8) {
      return "Password should be at least eight characters long.";
    }
    return "OK";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (pass !== confirmPass) {
      setError("The passwords you entered do not match.");
      return;
    }

    const resp = validatePassword(pass);
    if (resp !== "OK") {
      setError(resp);
      return;
    }

    setError("");
    user.actions.reset_password({
      new_password1: pass,
      new_password2: confirmPass,
      uid,
      token,
    });
  };

  return (
    <Box
      sx={{
        bgcolor: "#fcf6f6",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          flex: "1 0 auto",
          display: "flex",
          py: 0,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            bgcolor: "background.paper",
            boxShadow: 3,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="SmartTrail"
            sx={{ height: 48, display: "block", mb: 4 }}
          />

          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Stack>
              <TextField
                label="New Password"
                placeholder="New Password"
                type={showPass ? "text" : "password"}
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                fullWidth
                error={Boolean(error)}
                helperText={error || " "}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPass ? "Hide password" : "Show password"}
                        onClick={() => setShowPass((s) => !s)}
                        edge="end"
                      >
                        {showPass ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm New Password"
                placeholder="Confirm New Password"
                type={showConfirmPass ? "text" : "password"}
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                fullWidth
                error={Boolean(error)}
                helperText={error ? " " : " "}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showConfirmPass ? "Hide password" : "Show password"}
                        onClick={() => setShowConfirmPass((s) => !s)}
                        edge="end"
                      >
                        {showConfirmPass ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
              >
                <MUILink
                  component={RouterLink}
                  to="/login"
                  underline="hover"
                  sx={{ fontWeight: 600 }}
                >
                  Back to Login
                </MUILink>

                <Button variant="contained" type="submit">
                  Change Password
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Container>

      <LoginFooter />
    </Box>
  );
}
